import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Plus, Minus } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

export default function FAQPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [modalButtonType, setModalButtonType] = useState('demo');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleAccordion = (index) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    const faqSections = [
        {
            category: "General Questions",
            questions: [
                {
                    question: "What is Qorvix?",
                    answer: "Qorvix is an AI-powered automation platform that helps businesses streamline their workflows, connect customer touchpoints, and scale faster without writing code."
                },
                {
                    question: "Who is it for?",
                    answer: "Qorvix is designed for businesses of all sizes that want to automate lead response, customer communication, and sales processes across WhatsApp, Email, and CRM systems."
                },
                {
                    question: "How fast does it respond?",
                    answer: "Qorvix responds to new leads within 30 seconds across WhatsApp, Email, and DMs, ensuring no inquiry is missed."
                }
            ]
        },
        {
            category: "Setup & Technical",
            questions: [
                {
                    question: "Do i need to know code?",
                    answer: "No! Qorvix is a no-code platform. We build and configure everything for you, so you can focus on your business while we handle the technical setup."
                },
                {
                    question: "How long does setup take?",
                    answer: "Most setups are completed within 3-5 business days, depending on the complexity of your workflow and integrations required."
                },
                {
                    question: "Can it integrate with my existing tools?",
                    answer: "Yes! Qorvix integrates with popular platforms like Airtable, HubSpot, Notion, Google Sheets, WhatsApp Business API, Calendly, and many more."
                }
            ]
        },
        {
            category: "Features & Capabilities",
            questions: [
                {
                    question: "Can it handle multiple language?",
                    answer: "Yes, Qorvix supports multilingual conversations and can automatically detect and respond in the language your customers prefer."
                },
                {
                    question: "Can i customize the AI response?",
                    answer: "Absolutely! You can train the AI with your brand tone, FAQs, and specific messaging guidelines to ensure consistent, on-brand conversations."
                },
                {
                    question: "What happens if the AI doesnt know the answer?",
                    answer: "If the AI encounters a question it can't answer, it will automatically route the conversation to a human team member, ensuring seamless handoff without context loss."
                }
            ]
        },
        {
            category: "Security & Privacy",
            questions: [
                {
                    question: "How secure is my data?",
                    answer: "We use enterprise-grade encryption and follow industry-standard security practices to protect your data. All communications are encrypted in transit and at rest."
                },
                {
                    question: "Where is my data stored?",
                    answer: "Your data is securely stored on cloud servers with redundant backups. We comply with GDPR and other data protection regulations."
                }
            ]
        },
        {
            category: "Pricing & Support",
            questions: [
                {
                    question: "What are the pricing plans?",
                    answer: "We offer flexible pricing starting from £499 setup + £99/month for our Starter plan. Visit our pricing page to see all available plans and features."
                },
                {
                    question: "Is there a free trial?",
                    answer: "We offer a free demo and consultation to show you how Qorvix works for your specific business. Contact us to schedule your demo."
                },
                {
                    question: "What support option are availble?",
                    answer: "All plans include email support. Growth and Pro plans include priority support, and Enterprise customers get dedicated account management."
                }
            ]
        },
        {
            category: "Team & Operations",
            questions: [
                {
                    question: "Will it replace my staff?",
                    answer: "No, Qorvix is designed to empower your team, not replace them. It handles repetitive tasks so your staff can focus on high-value conversations and closing deals."
                },
                {
                    question: "Can my team still see all the conversation?",
                    answer: "Yes! Your team has full visibility into all conversations through a centralized dashboard where they can monitor, review, and take over chats when needed."
                },
                {
                    question: "What metrics can i track?",
                    answer: "You can track response times, conversion rates, lead quality scores, appointment bookings, chat volumes, team performance, and revenue attribution in real-time."
                }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .accordion-item {
          transition: all 0.3s ease;
        }

        .accordion-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .accordion-content.open {
          max-height: 500px;
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-2">
                            <img
                                src="/logo-qorvix.png"
                                alt="Qorvix Logo"
                                className="h-10 w-auto"
                            />
                        </Link>
                        {/* Desktop Menu */}
                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                {/* Dropdown Menu */}
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <a href="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</a>
                                        <a href="/integrations" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</a>
                                        <Link to="/faq" className="block px-4 py-2.5 text-orange-500 bg-gray-50 transition-colors">FAQs</Link>
                                        <a href="/help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</a>
                                        <a href="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</a>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]">
                                Start free demo
                            </button>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900"
                        >
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Modal - Add this after </nav> */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>

                    {/* Menu Panel */}
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        {/* Close Button */}
                        <button
                            onClick={() => setMobileMenuOpen(false)}
                            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X size={24} />
                        </button>

                        {/* Menu Items */}
                        <nav className="mt-4 space-y-6">
                            <Link
                                to="/"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Home
                            </Link>
                            <Link
                                to="/about"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                About
                            </Link>
                            <Link
                                to="/how-it-works"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                How it works
                            </Link>
                            <Link
                                to="/portfolio"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                portfolio
                            </Link>

                            {/* Resources Dropdown */}
                            <div>
                                <button className="w-full text-left text-lg font-medium text-gray-900 flex items-center gap-2">
                                    resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="mt-1">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="mt-4 ml-4 space-y-4">
                                    <Link
                                        to="/documentation"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Documentation
                                    </Link>
                                    <Link
                                        to="/integrations"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Integrations
                                    </Link>
                                    <Link
                                        to="/faq"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        FAQs
                                    </Link>
                                    <Link
                                        to="/help-center"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Help center
                                    </Link>
                                    <Link
                                        to="/roadmap"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Roadmap
                                    </Link>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Contact us
                            </Link>

                            {/* CTA Button */}
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">
                                Start free demo
                            </button>
                        </nav>
                    </div>
                </div>
            )}
            {/* Hero Section */}
            <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-orange-50 via-white to-orange-50">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        Frequently<br />Asked Questions
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Get quick answers about Qorvix setup, pricing, security, and features
                    </p>
                </div>
            </section>

            {/* FAQ Accordion Sections */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    {faqSections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="mb-12">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">
                                {section.category}
                            </h3>
                            <div className="space-y-4">
                                {section.questions.map((item, questionIndex) => {
                                    const globalIndex = `${sectionIndex}-${questionIndex}`;
                                    const isOpen = openAccordion === globalIndex;

                                    return (
                                        <div
                                            key={questionIndex}
                                            className="accordion-item bg-white border border-gray-200 rounded-lg overflow-hidden"
                                        >
                                            <button
                                                onClick={() => toggleAccordion(globalIndex)}
                                                className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                                            >
                                                <span className="text-left font-semibold text-gray-900">
                                                    {item.question}
                                                </span>
                                                <div className="flex-shrink-0 ml-4 w-6 h-6 border-2 border-gray-900 rounded flex items-center justify-center">
                                                    {isOpen ? (
                                                        <Minus size={14} className="text-gray-900" strokeWidth={3} />
                                                    ) : (
                                                        <Plus size={14} className="text-gray-900" strokeWidth={3} />
                                                    )}
                                                </div>
                                            </button>
                                            <div className={`accordion-content ${isOpen ? 'open' : ''}`}>
                                                <div className="px-6 pb-4">
                                                    <p className="text-gray-600 leading-relaxed">
                                                        {item.answer}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Still Have Questions Section */}
            <section className="py-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-orange-300 to-purple-400"></div>
                <div className="max-w-4xl mx-auto relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
                        Still Have Questions?
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Our Team Is Here To Help. Book A Free Demo Or Reach Out To Our Support Team.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/contact"
                            className="bg-orange-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-orange-700 transition-all"
                        >
                            Contact Support
                        </Link>
                        <Link
                            to="/help-center"
                            className="border-2 border-white bg-white text-gray-800 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all"
                        >
                            Visit Help Center
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                                <Link to="/how-it-works" className="block text-gray-600 hover:text-gray-900 transition-colors">How it works</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Documentation
                                </Link>

                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Integration
                                </Link>

                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    FAQs
                                </Link>

                                <Link to="/help-center" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Help Center
                                </Link>

                                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Roadmap
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
                        </div>
                    </div>
                </div>
            </footer>
            {/* Signup Modal */}
            <SignupFormModal
                isOpen={isSignupModalOpen}
                onClose={() => setIsSignupModalOpen(false)}
                buttonType={modalButtonType}
            />
        </div>
    );
}